/**
 * Enterprise Navigation Drawer - Extends BaseDrawer
 *
 * Navigation-specific implementation for mobile hamburger menu drawer.
 * Reuses BaseDrawer for common drawer behavior (focus trap, scroll lock, overlay, etc.)
 *
 * Features:
 * - Menu item stagger animation (30ms delay per item)
 * - Submenu toggles with +/- icons
 * - Header position calculation on open/resize
 * - Breakpoint-aware (1200px)
 * - Collection promo images support
 *
 * @extends BaseDrawer
 * @version 2.0.0 (Enterprise)
 * @date October 26, 2025
 */

class EnterpriseNavDrawer extends BaseDrawer {
  constructor() {
    super();

    // Navigation-specific elements
    this.mainDetailsToggle = this.querySelector('details');
    this.summaryElement = this.mainDetailsToggle?.querySelector('summary');
    this.menuDrawerElement = this.mainDetailsToggle?.querySelector('#menu-drawer');
    this.header = null;
    this.borderOffset = null;
    this.isClosing = false;

    // Bind navigation-specific methods
    this._boundHandleDocumentClick = this._handleDocumentClick.bind(this);
    this._boundStopPropagation = this._stopPropagation.bind(this);
    this._boundHandleEscapeKey = this._handleEscapeKey.bind(this);
    this._boundOnResize = this.onResize.bind(this);

    // Error checking
    if (!this.mainDetailsToggle) {
      window.debugLogger.error('EnterpriseNavDrawer: <details> element not found');
      return;
    }

    if (!this.summaryElement) {
      window.debugLogger.error('EnterpriseNavDrawer: <summary> element not found within <details>');
      return;
    }

    // Find overlay (from BaseDrawer markup)
    this.overlay = this.querySelector('[data-drawer-overlay]');

    // Find close button
    this.closeButton = this.mainDetailsToggle.querySelector(
      '.menu-drawer__close-button, .menu-drawer__header .menu-drawer__close, button[aria-label*="Close"], button[aria-label*="close"]'
    );

    // Set up event listeners
    this._setupEventListeners();

    // Initialize aria state
    this._syncInitialAriaState();
  }

  /**
   * Set up all event listeners
   * @private
   */
  _setupEventListeners() {
    // Details toggle event
    this.mainDetailsToggle.addEventListener('toggle', this._onToggleStateChange.bind(this));

    // Summary click (hamburger icon)
    if (this.summaryElement) {
      this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this._requestClose(event);
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) {
          this._requestClose(event);
        }
      });
    }
  }

  /**
   * Sync initial aria-expanded state
   * @private
   */
  _syncInitialAriaState() {
    if (this.summaryElement) {
      this.summaryElement.setAttribute(
        'aria-expanded',
        this.mainDetailsToggle.hasAttribute('open')
      );
    }
  }

  /**
   * Handle summary click (hamburger icon)
   */
  onSummaryClick(event) {
    event.preventDefault();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this._requestClose(event);
    } else {
      this.mainDetailsToggle.setAttribute('open', '');
    }
  }

  /**
   * Handle details toggle state change
   * @private
   */
  _onToggleStateChange() {
    this._syncInitialAriaState();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this._onDrawerOpened();
    } else {
      this._onDrawerClosed(this.summaryElement);
    }
  }

  /**
   * Drawer opened - Navigation-specific logic + BaseDrawer
   * @private
   */
  _onDrawerOpened() {
    // Get header reference
    this.header = this.header || document.querySelector('.section-header');

    // Store current scroll position before locking
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Header integration - calculate header bottom position
    if (this.header) {
      this.borderOffset = this.borderOffset ??
        (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );

      this.header.classList.add('menu-open');
    }

    // Add navigation-specific classes
    this.mainDetailsToggle.classList.add('menu-opening');

    // Breakpoint-aware body classes
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.add('menu-is-active');
    document.body.classList.add('menu-drawer-open');

    // Store scroll position in data attribute for reference
    document.body.dataset.scrollPosition = this.scrollPosition;

    // Activate overlay
    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }

    // Animate menu drawer and items
    if (this.menuDrawerElement) {
      requestAnimationFrame(() => {
        this.menuDrawerElement.classList.add('active');
        this._animateMenuItems(true); // Stagger animation
      });
    }

    // Set up document click listener (click outside to close)
    document.addEventListener('click', this._boundHandleDocumentClick, true);

    // Set up click handler on drawer content for submenu toggles
    this.drawerContentElement = this.mainDetailsToggle.querySelector('.menu-drawer__navigation-container')
      || this.mainDetailsToggle.querySelector('.menu-drawer__content');

    if (!this.drawerContentElement) {
      this.drawerContentElement = this.mainDetailsToggle;
    }

    if (this.drawerContentElement) {
      this.drawerContentElement.addEventListener('click', this._boundStopPropagation);
    }

    // Use BaseDrawer's focus trap
    trapFocus(this.mainDetailsToggle, this.summaryElement);

    // Escape key handling
    document.addEventListener('keydown', this._boundHandleEscapeKey);

    // Resize handling for header position
    window.addEventListener('resize', this._boundOnResize);
  }

  /**
   * Drawer closed - Navigation-specific cleanup + BaseDrawer
   * @private
   */
  _onDrawerClosed(elementToFocus = this.summaryElement) {
    // Remove navigation-specific classes
    this.mainDetailsToggle.classList.remove('menu-opening');

    // Remove active class from menu drawer
    if (this.menuDrawerElement) {
      this.menuDrawerElement.classList.remove('active');
    }

    // Remove header class
    if (this.header) {
      this.header.classList.remove('menu-open');
    }

    // Remove breakpoint-aware body classes
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove('menu-is-active');
    document.body.classList.remove('menu-drawer-open');

    // Restore scroll position if it was stored
    const storedPosition = document.body.dataset.scrollPosition;
    if (storedPosition) {
      window.scrollTo(0, parseInt(storedPosition));
      delete document.body.dataset.scrollPosition;
    }

    // Deactivate overlay
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
    }

    // Remove focus trap
    removeTrapFocus(elementToFocus);

    // Remove event listeners
    document.removeEventListener('keydown', this._boundHandleEscapeKey);
    window.removeEventListener('resize', this._boundOnResize);

    if (this.drawerContentElement) {
      this.drawerContentElement.removeEventListener('click', this._boundStopPropagation);
    }
  }

  /**
   * Animate menu items with stagger effect (navigation-specific)
   * @param {boolean} show - True to show items, false to hide
   * @private
   */
  _animateMenuItems(show) {
    // Target all primary list items in the main navigation and utility links sections
    const menuItems = this.mainDetailsToggle.querySelectorAll(
      '#menu-drawer .menu-drawer__navigation > .menu-drawer__menu > li, ' +
      '#menu-drawer .menu-drawer__utility-links > .list-menu > li'
    );

    const baseDelay = 30; // ms, reduced for a slightly faster, more subtle stagger
    let count = 0;

    menuItems.forEach((item) => {
      // Check if the item is a direct child of its respective list container,
      // to avoid animating nested submenu items unintentionally here.
      const parentList = item.parentElement;

      if (parentList &&
          (parentList.classList.contains('menu-drawer__menu') ||
           parentList.classList.contains('list-menu'))) {

        item.style.transitionDelay = show ? `${count * baseDelay}ms` : '0ms';

        if (show) {
          item.classList.add('item-visible');
        } else {
          item.classList.remove('item-visible');
        }

        count++;
      }
    });
  }

  /**
   * Handle document click (click outside to close)
   * @private
   */
  _handleDocumentClick(event) {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      document.removeEventListener('click', this._boundHandleDocumentClick, true);
      return;
    }

    const isClickOnSummary = this.summaryElement && this.summaryElement.contains(event.target);
    const isClickInsideDetailsElement = this.mainDetailsToggle.contains(event.target);

    if (isClickOnSummary) {
      // Let the default <details> toggle handle this
      return;
    }

    if (isClickInsideDetailsElement) {
      // Click is inside the drawer panel but not the summary
      // The _stopPropagation on drawerContentElement should prevent most of these
      return;
    }

    // If we reach here, the click was outside the <details> element
    // This is a true "click outside"
    event.preventDefault();
    event.stopPropagation();

    this._requestClose(event);
  }

  /**
   * Stop event propagation and handle submenu toggles (navigation-specific)
   * @private
   */
  _stopPropagation(event) {
    // Check if the click was on our toggleable link (+/- icons)
    const toggleableLink = event.target.closest('a.menu-drawer__menu-item-link--toggleable');

    if (toggleableLink) {
      event.preventDefault(); // Prevent link navigation for the toggle

      const targetId = toggleableLink.getAttribute('aria-controls');
      // Ensure submenuContent is searched for within the global document scope
      const submenuContent = document.getElementById(targetId);

      if (submenuContent) {
        const isCurrentlyExpanded = toggleableLink.getAttribute('aria-expanded') === 'true';
        toggleableLink.setAttribute('aria-expanded', String(!isCurrentlyExpanded));

        if (submenuContent.classList.contains('is-open')) {
          submenuContent.classList.remove('is-open');
          submenuContent.style.display = 'none';
        } else {
          submenuContent.classList.add('is-open');
          submenuContent.style.display = 'block';
        }
      }

      // We've handled the toggle, stop propagation to prevent menu close
      event.stopPropagation();
      return;
    }

    // If it wasn't a toggle link, just stop propagation
    // to prevent clicks inside the content from closing the drawer
    event.stopPropagation();
  }

  /**
   * Request close with animation
   * @private
   */
  _requestClose(event) {
    if (event &&
        event.type === 'click' &&
        this.mainDetailsToggle.contains(event.target) &&
        event.target !== this.overlay &&
        !event.target.closest('.menu-drawer__close-button')) {
      // Click inside drawer content (not on close triggers) - do nothing
      return;
    }

    if (this.mainDetailsToggle.hasAttribute('open') && !this.isClosing) {
      if (!this.menuDrawerElement) return;

      this.isClosing = true;

      // Animate items out
      this._animateMenuItems(false);

      // Remove active class to trigger CSS transition
      this.menuDrawerElement.classList.remove('active');

      // Wait for transition to complete
      const onTransitionEnd = (e) => {
        if (e.target === this.menuDrawerElement &&
            (e.propertyName === 'transform' || e.propertyName === 'opacity')) {

          this.menuDrawerElement.removeEventListener('transitionend', onTransitionEnd);

          if (this.mainDetailsToggle.hasAttribute('open')) {
            this.mainDetailsToggle.removeAttribute('open');
          }

          this.isClosing = false;
        }
      };

      this.menuDrawerElement.addEventListener('transitionend', onTransitionEnd);

      // Fallback timeout in case transitionend doesn't fire
      const transitionDuration = this.menuDrawerElement.style.transitionDuration ||
        getComputedStyle(this.menuDrawerElement).transitionDuration;

      const timeoutDuration = transitionDuration ?
        parseFloat(transitionDuration.replace('s', '')) * 1000 + 150 :
        600; // Default to 600ms if undefined

      setTimeout(() => {
        if (this.isClosing) {
          this.menuDrawerElement.removeEventListener('transitionend', onTransitionEnd);

          if (this.mainDetailsToggle.hasAttribute('open')) {
            this.mainDetailsToggle.removeAttribute('open');
          }

          this.isClosing = false;
        }
      }, timeoutDuration);
    }
  }

  /**
   * Handle Escape key to close drawer
   * @private
   */
  _handleEscapeKey(event) {
    if (event.code.toUpperCase() === 'ESCAPE' &&
        this.mainDetailsToggle.hasAttribute('open')) {
      this._requestClose(event);
    }
  }

  /**
   * Handle window resize (navigation-specific)
   * Updates header position and viewport height
   */
  onResize() {
    if (this.header) {
      this.borderOffset = this.borderOffset ??
        (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    }

    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  }

  /**
   * Public method to open the menu drawer
   * @param {HTMLElement} summaryElement - The summary element that triggered the open
   */
  openMenuDrawer(summaryElement) {
    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
    }
  }

  /**
   * Public method to close the menu drawer
   * @param {Event} event - The event that triggered the close
   * @param {HTMLElement} elementToFocus - Element to focus after closing
   */
  closeMenuDrawer(event, elementToFocus) {
    this._requestClose(event);
  }

  /**
   * Override BaseDrawer's onFocusOut to be intentionally empty
   * Document click listener and escape key should handle outside interactions
   */
  onFocusOut() {
    // Intentionally empty
    // The document click listener and escape key should handle outside interactions
  }
}

// Don't define the custom element here - will be done in header-drawer.liquid with feature flag
